/**
 * Avatar Upload Component (Story 1.9)
 *
 * Allows users to upload a profile avatar image.
 * Supports:
 * - Click-to-upload or drag-and-drop
 * - Client-side validation (file type, max 5MB)
 * - Upload progress indicator
 * - Flow: Get presigned URL → Upload to R2 → Confirm via API
 * - Optimistic UI update with preview
 */

import { useState, useRef, useEffect } from "react";
import { Avatar, AvatarImage, AvatarFallback, cn } from "@provenleap/ui";
import { apiPost, apiPatch } from "../lib/apiClient";
import { CameraIcon, LoadingSpinner } from "./icons";

// Allowed file types
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

interface AvatarUploadProps {
  currentUrl: string | null;
  email: string;
  displayName: string | null;
  onUploadComplete: (newUrl: string) => void;
  onError?: (message: string) => void;
  className?: string;
}

/**
 * Get initials from display name or email
 */
function getInitials(displayName: string | null, email: string): string {
  if (displayName) {
    const parts = displayName.split(" ");
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
  }
  return email.substring(0, 2).toUpperCase();
}

export function AvatarUpload({
  currentUrl,
  email,
  displayName,
  onUploadComplete,
  onError,
  className,
}: AvatarUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);

  // Cleanup preview URL on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const initials = getInitials(displayName, email);

  const handleFileSelect = async (file: File) => {
    // Client-side validation
    if (!ALLOWED_TYPES.includes(file.type)) {
      onError?.("Please upload a JPEG, PNG, or WebP image");
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      onError?.("Image must be less than 5MB");
      return;
    }

    // Revoke previous preview URL if exists
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    previewUrlRef.current = previewUrl;
    setPreview(previewUrl);
    setIsUploading(true);

    try {
      // Step 1: Get presigned URL
      const uploadUrlResponse = await apiPost("/api/user/avatar/upload-url", {
        contentType: file.type,
        fileSize: file.size,
      });

      if (!uploadUrlResponse.ok) {
        const error = await uploadUrlResponse.json();
        throw new Error(error.detail || "Failed to get upload URL");
      }

      const { data } = await uploadUrlResponse.json();
      const { uploadUrl, avatarUrl } = data;

      // Step 2: Upload directly to R2
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload image");
      }

      // Step 3: Confirm upload to backend
      const confirmResponse = await apiPatch("/api/user/avatar", {
        avatarUrl,
      });

      if (!confirmResponse.ok) {
        const error = await confirmResponse.json();
        throw new Error(error.detail || "Failed to confirm upload");
      }

      // Success - update UI
      onUploadComplete(avatarUrl);
    } catch (error) {
      console.error("[AvatarUpload] Error:", error);
      setPreview(null);
      onError?.(error instanceof Error ? error.message : "Failed to upload avatar");
    } finally {
      setIsUploading(false);
      // Cleanup preview URL (ref handles unmount case)
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  const displayUrl = preview || currentUrl;

  return (
    <div className={cn("flex flex-col items-center gap-4", className)}>
      <div
        className={cn(
          "relative cursor-pointer group",
          isDragging && "ring-2 ring-primary ring-offset-2 rounded-full"
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        role="button"
        tabIndex={0}
        aria-label="Upload avatar"
      >
        <Avatar className="h-24 w-24">
          {displayUrl ? (
            <AvatarImage src={displayUrl} alt="Profile avatar" />
          ) : null}
          <AvatarFallback className="text-lg bg-muted">
            {initials}
          </AvatarFallback>
        </Avatar>

        {/* Upload overlay */}
        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
          <CameraIcon className="h-6 w-6 text-white" />
        </div>

        {/* Loading spinner */}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
            <LoadingSpinner className="h-6 w-6 text-white" />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleInputChange}
        className="sr-only"
        aria-label="Choose avatar file"
      />

      <p className="text-sm text-muted-foreground text-center">
        Click or drag to upload a new avatar
        <br />
        <span className="text-xs">JPEG, PNG, or WebP - Max 5MB</span>
      </p>
    </div>
  );
}
